import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { formatName } from '@/lib/utils';
import { Download, CreditCard, Plus, XCircle, Pencil, Trash2, Search, Filter, RefreshCw, TrendingUp, Ban, Wallet, ChevronLeft, ChevronRight, ArrowUpDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const PAGE_SIZE = 25;

export default function TransactionsPage() {
  const { user } = useAuth();
  const isClient = user?.role === 'CLIENT';
  const isAdmin = user?.role === 'ADMIN';
  const isTherapist = user?.role === 'THERAPIST';

  const [transactions, setTransactions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState({ total: 0, count: 0, month_total: 0, cancelled_total: 0, cancelled_count: 0 });
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [showEditModal, setShowEditModal] = useState(null);
  const [editForm, setEditForm] = useState({ amount: '', payment_mode: '', payment_date: '', description: '' });
  const [showDeleteTxModal, setShowDeleteTxModal] = useState(null);
  const [clients, setClients] = useState([]);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({ client_id: '', amount: '', payment_mode: 'CASH', payment_date: new Date().toISOString().split('T')[0], description: '' });

  // Filters
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    payment_mode: 'ALL',
    status: 'ALL',
    branch_id: 'ALL',
    receipt_status: 'ALL',
    search: '',
  });
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState({ key: 'payment_date', dir: 'desc' });

  const buildQuery = (extras = {}) => {
    const params = new URLSearchParams();
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.payment_mode && filters.payment_mode !== 'ALL') params.append('payment_mode', filters.payment_mode);
    if (filters.status && filters.status !== 'ALL') params.append('status', filters.status);
    if (filters.receipt_status && filters.receipt_status !== 'ALL') params.append('receipt_status', filters.receipt_status);
    if (isAdmin && filters.branch_id && filters.branch_id !== 'ALL') params.append('branch_id', filters.branch_id);
    if (filters.search.trim()) params.append('search', filters.search.trim());
    Object.entries(extras).forEach(([k, v]) => params.append(k, v));
    return params.toString();
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const qs = buildQuery({ skip: page * PAGE_SIZE, limit: PAGE_SIZE });
      const sumQs = buildQuery();
      const [tRes, sRes] = await Promise.all([
        api.get(`/transactions?${qs}`),
        api.get(`/transactions/summary?${sumQs}`),
      ]);
      // Backend returns { items, total } for paginated; if list, fall back
      const data = tRes.data;
      const items = Array.isArray(data) ? data : (data.items || []);
      const total = Array.isArray(data) ? items.length : (data.total ?? items.length);
      setTransactions(items);
      setTotalCount(total);
      setSummary(sRes.data);
    } catch {} finally {
      setLoading(false);
    }
  };

  const fetchSupporting = async () => {
    if (isClient) return;
    try {
      const cRes = await api.get('/clients');
      setClients(cRes.data || []);
    } catch {}
    if (isAdmin) {
      try {
        const bRes = await api.get('/branches');
        setBranches(bRes.data || []);
      } catch {}
    }
  };

  useEffect(() => { fetchSupporting(); /* eslint-disable-next-line */ }, [isClient, isAdmin]);
  useEffect(() => { fetchTransactions(); /* eslint-disable-next-line */ }, [page, filters, isClient]);

  const handleCreate = async (e) => {
    e.preventDefault();
    await api.post('/transactions', { ...form, amount: parseFloat(form.amount) });
    setShowModal(false);
    setForm({ client_id: '', amount: '', payment_mode: 'CASH', payment_date: new Date().toISOString().split('T')[0], description: '' });
    setPage(0);
    fetchTransactions();
  };

  const handleCancel = async () => {
    if (!showCancelModal || !cancelReason.trim()) return;
    try {
      await api.put(`/transactions/${showCancelModal._id}/cancel`, { reason: cancelReason.trim() });
      setShowCancelModal(null);
      setCancelReason('');
      fetchTransactions();
    } catch {}
  };

  const openEdit = (t) => {
    setEditForm({ amount: t.amount, payment_mode: t.payment_mode, payment_date: t.payment_date, description: t.description || '' });
    setShowEditModal(t);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!showEditModal) return;
    try {
      await api.put(`/transactions/${showEditModal._id}`, { ...editForm, amount: parseFloat(editForm.amount) });
      setShowEditModal(null);
      fetchTransactions();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to edit');
    }
  };

  const canEditTx = (t) => {
    if (t.status === 'CANCELLED') return false;
    if (typeof t.can_modify === 'boolean') return t.can_modify;
    if (isAdmin) return true;
    if (isTherapist && t.therapist_id === user._id) return true;
    return false;
  };

  const canDeleteTx = (t) => canEditTx(t);

  const handleDeleteTx = async () => {
    if (!showDeleteTxModal) return;
    try {
      await api.delete(`/transactions/${showDeleteTxModal._id}`);
      setShowDeleteTxModal(null);
      fetchTransactions();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const downloadInvoice = async (txId) => {
    const res = await api.get(`/transactions/${txId}/invoice`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a'); a.href = url; a.download = `receipt_${txId}.pdf`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const generateReceipt = async (txId) => {
    try {
      await api.post(`/transactions/${txId}/generate-receipt`);
      fetchTransactions();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to generate receipt');
    }
  };

  const resetFilters = () => {
    setFilters({ date_from: '', date_to: '', payment_mode: 'ALL', status: 'ALL', branch_id: 'ALL', receipt_status: 'ALL', search: '' });
    setPage(0);
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  // Client-side sort within current page
  const sortedTx = useMemo(() => {
    const arr = [...transactions];
    arr.sort((a, b) => {
      const k = sortBy.key;
      let va = a[k], vb = b[k];
      if (k === 'amount') { va = Number(va || 0); vb = Number(vb || 0); }
      else { va = (va ?? '').toString().toLowerCase(); vb = (vb ?? '').toString().toLowerCase(); }
      if (va < vb) return sortBy.dir === 'asc' ? -1 : 1;
      if (va > vb) return sortBy.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [transactions, sortBy]);

  const toggleSort = (key) => {
    setSortBy(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const modeColor = { CASH: 'bg-[#2E8B57]/10 text-[#2E8B57]', UPI: 'bg-[#0D8B7D]/10 text-[#0D8B7D]', CARD: 'bg-[#F5B041]/10 text-[#F5B041]', BANK_TRANSFER: 'bg-[#4A6B68]/10 text-[#4A6B68]', OTHER: 'bg-[#4A6B68]/10 text-[#4A6B68]' };

  // ----- CLIENT VIEW (with Transactions + Invoices tabs) -----
  if (isClient) {
    return <ClientTxAndInvoices sortedTx={sortedTx} modeColor={modeColor} downloadInvoice={downloadInvoice} />;
  }

  // ----- ADMIN / THERAPIST GLOBAL VIEW -----
  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A3330] font-['Outfit']">Transactions &amp; Receipts</h1>
          <p className="text-sm text-[#4A6B68] mt-0.5">Manage all transactions across {isAdmin ? 'the clinic' : 'your assigned clients'}</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="add-transaction-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Transaction
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="tx-kpi-grid">
        <Card className="bg-white border-[#E5F2F1] rounded-2xl" data-testid="kpi-total-revenue">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#0D8B7D]/10 flex items-center justify-center"><Wallet className="w-6 h-6 text-[#0D8B7D]" /></div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-[#4A6B68] font-medium">Total Revenue</p>
              <p className="text-xl font-semibold text-[#1A3330] font-['Outfit']">Rs.{(summary.total || 0).toLocaleString()}</p>
              <p className="text-[11px] text-[#4A6B68]">{summary.count || 0} active</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5F2F1] rounded-2xl" data-testid="kpi-month-revenue">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#F5B041]/10 flex items-center justify-center"><TrendingUp className="w-6 h-6 text-[#F5B041]" /></div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-[#4A6B68] font-medium">This Month</p>
              <p className="text-xl font-semibold text-[#1A3330] font-['Outfit']">Rs.{(summary.month_total || 0).toLocaleString()}</p>
              <p className="text-[11px] text-[#4A6B68]">Current period</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5F2F1] rounded-2xl" data-testid="kpi-tx-count">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#2E8B57]/10 flex items-center justify-center"><CreditCard className="w-6 h-6 text-[#2E8B57]" /></div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-[#4A6B68] font-medium">Active Transactions</p>
              <p className="text-xl font-semibold text-[#1A3330] font-['Outfit']">{summary.count || 0}</p>
              <p className="text-[11px] text-[#4A6B68]">Total in scope</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5F2F1] rounded-2xl" data-testid="kpi-cancelled">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#D3544E]/10 flex items-center justify-center"><Ban className="w-6 h-6 text-[#D3544E]" /></div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-[#4A6B68] font-medium">Cancelled</p>
              <p className="text-xl font-semibold text-[#1A3330] font-['Outfit']">{summary.cancelled_count || 0}</p>
              <p className="text-[11px] text-[#4A6B68]">Rs.{(summary.cancelled_total || 0).toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white border-[#E5F2F1] rounded-2xl" data-testid="tx-filters">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 text-[#1A3330]">
            <Filter className="w-4 h-4" /><span className="text-sm font-semibold font-['Outfit']">Filters</span>
            <Button variant="ghost" size="sm" onClick={resetFilters} className="ml-auto text-[#4A6B68] hover:text-[#0D8B7D]" data-testid="reset-filters-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reset
            </Button>
          </div>
          <div className={`grid grid-cols-1 sm:grid-cols-2 ${isAdmin ? 'lg:grid-cols-7' : 'lg:grid-cols-6'} gap-3`}>
            <div>
              <Label className="text-xs text-[#4A6B68]">Search Client</Label>
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-[#4A6B68] absolute left-3 top-1/2 -translate-y-1/2" />
                <Input placeholder="Name or email" value={filters.search} onChange={e => updateFilter('search', e.target.value)} className="rounded-xl border-[#E5F2F1] pl-8 h-9 text-sm" data-testid="filter-search" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#4A6B68]">From</Label>
              <Input type="date" value={filters.date_from} onChange={e => updateFilter('date_from', e.target.value)} className="rounded-xl border-[#E5F2F1] h-9 text-sm" data-testid="filter-date-from" />
            </div>
            <div>
              <Label className="text-xs text-[#4A6B68]">To</Label>
              <Input type="date" value={filters.date_to} onChange={e => updateFilter('date_to', e.target.value)} className="rounded-xl border-[#E5F2F1] h-9 text-sm" data-testid="filter-date-to" />
            </div>
            <div>
              <Label className="text-xs text-[#4A6B68]">Payment Mode</Label>
              <Select value={filters.payment_mode} onValueChange={v => updateFilter('payment_mode', v)}>
                <SelectTrigger className="rounded-xl border-[#E5F2F1] h-9 text-sm" data-testid="filter-payment-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All modes</SelectItem>
                  <SelectItem value="CASH">Cash</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="CARD">Card</SelectItem>
                  <SelectItem value="BANK_TRANSFER">Bank Transfer</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#4A6B68]">Status</Label>
              <Select value={filters.status} onValueChange={v => updateFilter('status', v)}>
                <SelectTrigger className="rounded-xl border-[#E5F2F1] h-9 text-sm" data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#4A6B68]">Receipt Status</Label>
              <Select value={filters.receipt_status} onValueChange={v => updateFilter('receipt_status', v)}>
                <SelectTrigger className="rounded-xl border-[#E5F2F1] h-9 text-sm" data-testid="filter-receipt-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All</SelectItem>
                  <SelectItem value="GENERATED">Receipt Generated</SelectItem>
                  <SelectItem value="NOT_GENERATED">No Receipt</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isAdmin && (
              <div>
                <Label className="text-xs text-[#4A6B68]">Branch</Label>
                <Select value={filters.branch_id} onValueChange={v => updateFilter('branch_id', v)}>
                  <SelectTrigger className="rounded-xl border-[#E5F2F1] h-9 text-sm" data-testid="filter-branch"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All branches</SelectItem>
                    {branches.map(b => <SelectItem key={b._id} value={b._id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white border-[#E5F2F1] rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4FAF9]">
              <TableHead className="text-[#4A6B68] text-xs font-semibold">Invoice</TableHead>
              <TableHead className="text-[#4A6B68] text-xs font-semibold cursor-pointer select-none" onClick={() => toggleSort('payment_date')} data-testid="sort-date">
                <span className="inline-flex items-center gap-1">Date <ArrowUpDown className="w-3 h-3" /></span>
              </TableHead>
              <TableHead className="text-[#4A6B68] text-xs font-semibold cursor-pointer select-none" onClick={() => toggleSort('client_name')} data-testid="sort-client">
                <span className="inline-flex items-center gap-1">Client <ArrowUpDown className="w-3 h-3" /></span>
              </TableHead>
              {isAdmin && <TableHead className="text-[#4A6B68] text-xs font-semibold">Branch</TableHead>}
              <TableHead className="text-[#4A6B68] text-xs font-semibold">Description</TableHead>
              <TableHead className="text-[#4A6B68] text-xs font-semibold">Mode</TableHead>
              <TableHead className="text-[#4A6B68] text-xs font-semibold">Linked Invoice</TableHead>
              <TableHead className="text-[#4A6B68] text-xs font-semibold">Status</TableHead>
              <TableHead className="text-[#4A6B68] text-xs font-semibold">Receipt</TableHead>
              <TableHead className="text-[#4A6B68] text-xs font-semibold text-right cursor-pointer select-none" onClick={() => toggleSort('amount')} data-testid="sort-amount">
                <span className="inline-flex items-center gap-1 justify-end">Amount <ArrowUpDown className="w-3 h-3" /></span>
              </TableHead>
              <TableHead className="text-[#4A6B68] text-xs font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedTx.map(t => {
              const isCancelled = t.status === 'CANCELLED';
              const receiptGen = !!t.receipt_generated;
              return (
                <TableRow key={t._id} className={`hover:bg-[#F4FAF9] ${isCancelled ? 'opacity-70' : ''}`} data-testid={`tx-row-${t._id}`}>
                  <TableCell className="text-xs text-[#1A3330] font-medium">{t.invoice_number}</TableCell>
                  <TableCell className="text-xs text-[#4A6B68]">{t.payment_date}</TableCell>
                  <TableCell className="text-xs text-[#1A3330]">{t.client_name || '-'}</TableCell>
                  {isAdmin && <TableCell className="text-xs text-[#4A6B68]">{t.branch_name || '-'}</TableCell>}
                  <TableCell className="text-xs text-[#1A3330] max-w-[200px] truncate">{t.description}</TableCell>
                  <TableCell><Badge className={`${modeColor[t.payment_mode] || ''} text-[10px] border-0`}>{t.payment_mode}</Badge></TableCell>
                  <TableCell className="text-xs text-[#4A6B68]">{t.linked_invoice_number || <span className="italic">Standalone</span>}</TableCell>
                  <TableCell>
                    {isCancelled ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Badge className="bg-[#D3544E]/10 text-[#D3544E] text-[10px] border-0 cursor-help" data-testid={`cancelled-badge-${t._id}`}>Cancelled</Badge>
                          </TooltipTrigger>
                          {isAdmin && t.cancellation_reason && (
                            <TooltipContent className="max-w-[250px] bg-white border border-[#E5F2F1] text-[#1A3330] text-xs">
                              <p className="font-medium">Reason:</p><p>{t.cancellation_reason}</p>
                              {t.cancelled_by_name && <p className="text-[#4A6B68] mt-1">By: {t.cancelled_by_name}</p>}
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <Badge className="bg-[#2E8B57]/10 text-[#2E8B57] text-[10px] border-0">Active</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {receiptGen
                      ? <Badge className="bg-[#2E8B57]/10 text-[#2E8B57] text-[10px] border-0" data-testid={`receipt-badge-${t._id}`}>Generated</Badge>
                      : <Badge className="bg-gray-100 text-gray-600 text-[10px] border-0">No Receipt</Badge>}
                  </TableCell>
                  <TableCell className={`text-xs font-semibold text-right ${isCancelled ? 'line-through text-[#D3544E]' : 'text-[#1A3330]'}`}>
                    Rs.{t.amount?.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 justify-end">
                      {receiptGen
                        ? <Button variant="ghost" size="sm" onClick={() => downloadInvoice(t._id)} data-testid={`download-receipt-${t._id}`}><Download className="w-4 h-4 text-[#0D8B7D]" /></Button>
                        : !isCancelled && <Button variant="ghost" size="sm" onClick={() => generateReceipt(t._id)} className="text-[10px] h-7 px-2" data-testid={`gen-receipt-${t._id}`}>Generate</Button>}
                      {canEditTx(t) && (
                        <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`edit-tx-${t._id}`}><Pencil className="w-3.5 h-3.5 text-[#0D8B7D]" /></Button>
                      )}
                      {isAdmin && !isCancelled && (
                        <Button variant="ghost" size="sm" onClick={() => { setShowCancelModal(t); setCancelReason(''); }} data-testid={`cancel-tx-${t._id}`}>
                          <XCircle className="w-4 h-4 text-[#D3544E]" />
                        </Button>
                      )}
                      {canDeleteTx(t) && (
                        <Button variant="ghost" size="sm" onClick={() => setShowDeleteTxModal(t)} data-testid={`delete-tx-${t._id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-[#D3544E]" />
                        </Button>
                      )}
                    </div>
                    {t.last_edited_at && <span className="text-[9px] text-[#F5B041] italic block text-right" data-testid={`edited-label-${t._id}`}>Edited</span>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {!loading && sortedTx.length === 0 && <div className="p-6 text-center text-sm text-[#4A6B68]" data-testid="no-tx-message">No transactions found.</div>}
        {loading && <div className="p-6 text-center text-sm text-[#4A6B68]">Loading...</div>}

        {/* Pagination */}
        {totalCount > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5F2F1] bg-[#F4FAF9]/50" data-testid="tx-pagination">
            <p className="text-xs text-[#4A6B68]">
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} className="rounded-xl border-[#E5F2F1] h-8" data-testid="prev-page-btn">
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>
              <span className="text-xs text-[#1A3330] font-medium">Page {page + 1} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)} className="rounded-xl border-[#E5F2F1] h-8" data-testid="next-page-btn">
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Add Transaction Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md bg-white rounded-2xl" data-testid="add-tx-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">Add Transaction</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div><Label>Client</Label>
              <Select value={form.client_id} onValueChange={v => setForm({...form, client_id: v})} required>
                <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="tx-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map(c => <SelectItem key={c._id} value={c._id}>{formatName(c)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Amount (INR)</Label><Input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required className="rounded-xl border-[#E5F2F1]" data-testid="tx-amount" /></div>
            <div><Label>Payment Mode</Label>
              <Select value={form.payment_mode} onValueChange={v => setForm({...form, payment_mode: v})}>
                <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="tx-mode"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="CASH">Cash</SelectItem><SelectItem value="UPI">UPI</SelectItem><SelectItem value="CARD">Card</SelectItem><SelectItem value="BANK_TRANSFER">Bank Transfer</SelectItem><SelectItem value="OTHER">Other</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input type="date" value={form.payment_date} onChange={e => setForm({...form, payment_date: e.target.value})} className="rounded-xl border-[#E5F2F1]" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} required className="rounded-xl border-[#E5F2F1]" data-testid="tx-desc" /></div>
            <Button type="submit" className="w-full bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="tx-create-btn">Add Transaction</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cancel Transaction Modal (Admin only) */}
      <Dialog open={!!showCancelModal} onOpenChange={() => setShowCancelModal(null)}>
        <DialogContent className="max-w-md bg-white rounded-2xl" data-testid="cancel-tx-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">Cancel Transaction</DialogTitle></DialogHeader>
          {showCancelModal && (
            <div className="space-y-4">
              <div className="bg-[#D3544E]/5 p-4 rounded-xl space-y-1">
                <p className="text-sm font-medium text-[#1A3330]">Invoice: {showCancelModal.invoice_number}</p>
                <p className="text-xs text-[#4A6B68]">Amount: Rs.{showCancelModal.amount?.toLocaleString()} · Date: {showCancelModal.payment_date}</p>
                <p className="text-xs text-[#4A6B68]">Client: {showCancelModal.client_name}</p>
              </div>
              <div>
                <Label className="text-sm text-[#1A3330]">Cancellation Reason <span className="text-[#D3544E]">*</span></Label>
                <Textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} required rows={3} className="rounded-xl border-[#E5F2F1] mt-1" placeholder="Provide reason for cancellation..." data-testid="cancel-tx-reason" />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCancel} disabled={!cancelReason.trim()} className="flex-1 bg-[#D3544E] hover:bg-[#C04440] text-white rounded-xl" data-testid="confirm-cancel-tx-btn">Confirm Cancellation</Button>
                <Button variant="outline" className="rounded-xl border-[#E5F2F1]" onClick={() => setShowCancelModal(null)}>Go Back</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Transaction Modal */}
      <Dialog open={!!showEditModal} onOpenChange={() => setShowEditModal(null)}>
        <DialogContent className="max-w-md bg-white rounded-2xl" data-testid="edit-tx-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">Edit Transaction</DialogTitle></DialogHeader>
          {showEditModal && (
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="bg-[#F4FAF9] p-3 rounded-xl">
                <p className="text-xs text-[#4A6B68]">Invoice: {showEditModal.invoice_number} (cannot be changed)</p>
              </div>
              <div><Label>Amount (INR)</Label><Input type="number" value={editForm.amount} onChange={e => setEditForm({...editForm, amount: e.target.value})} required className="rounded-xl border-[#E5F2F1]" data-testid="edit-tx-amount" /></div>
              <div><Label>Payment Mode</Label>
                <Select value={editForm.payment_mode} onValueChange={v => setEditForm({...editForm, payment_mode: v})}>
                  <SelectTrigger className="rounded-xl border-[#E5F2F1]"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="CASH">Cash</SelectItem><SelectItem value="UPI">UPI</SelectItem><SelectItem value="CARD">Card</SelectItem><SelectItem value="BANK_TRANSFER">Bank Transfer</SelectItem><SelectItem value="OTHER">Other</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Date</Label><Input type="date" value={editForm.payment_date} onChange={e => setEditForm({...editForm, payment_date: e.target.value})} className="rounded-xl border-[#E5F2F1]" /></div>
              <div><Label>Description</Label><Input value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} required className="rounded-xl border-[#E5F2F1]" data-testid="edit-tx-desc" /></div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1 bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="save-edit-tx-btn">Save Changes</Button>
                <Button type="button" variant="outline" className="rounded-xl border-[#E5F2F1]" onClick={() => setShowEditModal(null)}>Cancel</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Transaction Modal */}
      <Dialog open={!!showDeleteTxModal} onOpenChange={() => setShowDeleteTxModal(null)}>
        <DialogContent className="max-w-sm bg-white rounded-2xl" data-testid="delete-tx-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#D3544E]">Delete Transaction</DialogTitle></DialogHeader>
          {showDeleteTxModal && (
            <div className="space-y-4">
              <p className="text-sm text-[#4A6B68]">Are you sure you want to permanently delete this transaction? This action cannot be undone.</p>
              <div className="bg-[#D3544E]/5 p-3 rounded-xl">
                <p className="text-sm font-medium text-[#1A3330]">Invoice: {showDeleteTxModal.invoice_number}</p>
                <p className="text-xs text-[#4A6B68]">Amount: Rs.{showDeleteTxModal.amount?.toLocaleString()} · {showDeleteTxModal.client_name}</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleDeleteTx} className="flex-1 bg-[#D3544E] hover:bg-[#C04440] text-white rounded-xl" data-testid="confirm-delete-tx-btn">Confirm Delete</Button>
                <Button variant="outline" className="rounded-xl border-[#E5F2F1]" onClick={() => setShowDeleteTxModal(null)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ClientTxAndInvoices({ sortedTx, modeColor, downloadInvoice }) {
  const [tab, setTab] = useState('invoices');
  const [invoices, setInvoices] = useState([]);
  useEffect(() => {
    if (tab === 'invoices') {
      api.get('/invoices?limit=200').then(r => setInvoices(r.data.items || [])).catch(() => {});
    }
  }, [tab]);
  const downloadInvPdf = async (id) => {
    const res = await api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a'); a.href = url; a.download = `invoice_${id}.pdf`; a.click();
    window.URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-semibold text-[#1A3330] font-['Outfit']">Invoices &amp; Receipts</h1>
      </div>
      <div className="flex gap-1 border-b border-[#E5F2F1]" data-testid="client-tx-tabs">
        <button onClick={() => setTab('invoices')} data-testid="tab-invoices" className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'invoices' ? 'border-[#0D8B7D] text-[#0D8B7D]' : 'border-transparent text-[#4A6B68]'}`}>Invoices</button>
        <button onClick={() => setTab('transactions')} data-testid="tab-transactions" className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'transactions' ? 'border-[#0D8B7D] text-[#0D8B7D]' : 'border-transparent text-[#4A6B68]'}`}>Transactions &amp; Receipts</button>
      </div>
      {tab === 'transactions' && (
        <Card className="bg-white border-[#E5F2F1] rounded-2xl overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-[#F4FAF9]">
              <TableHead className="text-[#4A6B68] text-xs font-semibold">TXN Number</TableHead>
              <TableHead className="text-[#4A6B68] text-xs font-semibold">Date</TableHead>
              <TableHead className="text-[#4A6B68] text-xs font-semibold">Description</TableHead>
              <TableHead className="text-[#4A6B68] text-xs font-semibold">Mode</TableHead>
              <TableHead className="text-[#4A6B68] text-xs font-semibold">Receipt</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {sortedTx.map(t => {
                const isCancelled = t.status === 'CANCELLED';
                const receiptGen = !!t.receipt_generated;
                return (
                  <TableRow key={t._id} className={`hover:bg-[#F4FAF9] ${isCancelled ? 'opacity-70' : ''}`} data-testid={`tx-row-${t._id}`}>
                    <TableCell className="text-xs font-medium">{t.invoice_number}</TableCell>
                    <TableCell className="text-xs text-[#4A6B68]">{t.payment_date}</TableCell>
                    <TableCell className="text-xs">{t.description}</TableCell>
                    <TableCell><Badge className={`${modeColor[t.payment_mode] || ''} text-[10px] border-0`}>{t.payment_mode}</Badge></TableCell>
                    <TableCell>
                      {receiptGen
                        ? <Badge className="bg-[#2E8B57]/10 text-[#2E8B57] text-[10px] border-0">Generated</Badge>
                        : <Badge className="bg-gray-100 text-gray-600 text-[10px] border-0">Not Generated</Badge>}
                    </TableCell>
                    <TableCell>
                      {receiptGen && <Button variant="ghost" size="sm" onClick={() => downloadInvoice(t._id)} data-testid={`client-dl-rcp-${t._id}`}><Download className="w-4 h-4 text-[#0D8B7D]" /></Button>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {sortedTx.length === 0 && <div className="p-6 text-center text-sm text-[#4A6B68]">No transactions yet.</div>}
        </Card>
      )}
      {tab === 'invoices' && (
        <Card className="bg-white border-[#E5F2F1] rounded-2xl overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-[#F4FAF9]">
              <TableHead className="text-[#4A6B68] text-xs font-semibold">Invoice Number</TableHead>
              <TableHead className="text-[#4A6B68] text-xs font-semibold">Date</TableHead>
              <TableHead className="text-[#4A6B68] text-xs font-semibold">Status</TableHead>
              <TableHead className="text-[#4A6B68] text-xs font-semibold">Due Date</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {invoices.map(inv => (
                <TableRow key={inv._id} data-testid={`client-inv-row-${inv._id}`}>
                  <TableCell className="text-xs font-medium">{inv.invoice_number}</TableCell>
                  <TableCell className="text-xs">{inv.invoice_date}</TableCell>
                  <TableCell><Badge className="bg-[#0D8B7D]/10 text-[#0D8B7D] text-[10px] border-0">{inv.status?.replace('_',' ')}</Badge></TableCell>
                  <TableCell className="text-xs">{inv.due_date || '-'}</TableCell>
                  <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => downloadInvPdf(inv._id)} data-testid={`client-dl-inv-${inv._id}`}><Download className="w-4 h-4 text-[#0D8B7D]" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {invoices.length === 0 && <div className="p-6 text-center text-sm text-[#4A6B68]">No invoices.</div>}
        </Card>
      )}
    </div>
  );
}
